"use client";

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CirclePlay, X } from "lucide-react";
import { cn } from "~/lib/utils";

interface VideoLightboxProps {
  label: string;
  // One of the two: an uploaded mp4, or a YouTube video ID.
  videoUrl?: string;
  youtubeId?: string;
  className?: string;
}

// "Watch video" button that plays a Strapi-uploaded mp4 or a YouTube video
// in a lightbox.
// Radix Dialog gives Esc-to-close, focus trapping and scroll lock; the video
// only mounts while open, so closing it stops playback.
export const VideoLightbox = ({ label, videoUrl, youtubeId, className }: VideoLightboxProps) => (
  <DialogPrimitive.Root>
    <DialogPrimitive.Trigger className={className}>
      <CirclePlay aria-hidden="true" className="size-[22px]" />
      {label}
    </DialogPrimitive.Trigger>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-[101] w-[calc(100%-32px)] max-w-[1100px] -translate-x-1/2 -translate-y-1/2 outline-none",
          "data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        )}
      >
        <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
        <DialogPrimitive.Close
          aria-label="Close video"
          className="absolute -top-12 right-0 flex size-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
        >
          <X aria-hidden="true" className="size-6" />
        </DialogPrimitive.Close>
        {youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
            title={label}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="aspect-video w-full rounded-lg bg-black"
          />
        ) : (
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            className="aspect-video w-full rounded-lg bg-black"
          />
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);
